import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SelectListService } from '../../dynamic-forms/services/form-utility/select-list.service';
import { UserInputOptionType } from '../../shared-models/user-input-option-type';

export enum FilterDataType {
  String,
  Date,
  Number,
  Boolean,
  SystemId,
  Reference,
  None,
  DateTime,
}

export interface FilterOperation {
  value: UserInputOptionType;
  name: string;
  requiresUserValue: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FilterOperations {
  private selectListService = inject(SelectListService);

  public readonly CONTAINS: FilterOperation = {
    value: UserInputOptionType.Contains,
    name: 'contains',
    requiresUserValue: true,
  };
  public readonly DOES_NOT_CONTAIN: FilterOperation = {
    value: UserInputOptionType.DoesNotContain,
    name: 'does not contain',
    requiresUserValue: true,
  };
  public readonly EQUALS: FilterOperation = {
    value: UserInputOptionType.Equals,
    name: 'equals',
    requiresUserValue: true,
  };
  public readonly DOES_NOT_EQUAL: FilterOperation = {
    value: UserInputOptionType.DoesNotEqual,
    name: 'does not equal',
    requiresUserValue: true,
  };
  public readonly LESS_THAN: FilterOperation = {
    value: UserInputOptionType.LessThan,
    name: 'less than',
    requiresUserValue: true,
  };
  public readonly LESS_THAN_OR_EQUAL: FilterOperation = {
    value: UserInputOptionType.LessThanOrEqual,
    name: 'less than or equal to',
    requiresUserValue: true,
  };
  public readonly GREATER_THAN: FilterOperation = {
    value: UserInputOptionType.GreaterThan,
    name: 'greater than',
    requiresUserValue: true,
  };
  public readonly GREATER_THAN_OR_EQUAL: FilterOperation = {
    value: UserInputOptionType.GreaterThanOrEqual,
    name: 'greater than or equal to',
    requiresUserValue: true,
  };
  public readonly RANGE: FilterOperation = {
    value: UserInputOptionType.InRange,
    name: 'in range',
    requiresUserValue: true,
  };
  public readonly NOTINRANGE: FilterOperation = {
    value: UserInputOptionType.NotInRange,
    name: 'does not equal',
    requiresUserValue: true,
  };
  public readonly EMPTY: FilterOperation = {
    value: UserInputOptionType.Empty,
    name: 'is empty',
    requiresUserValue: false,
  };
  public readonly NOT_EMPTY: FilterOperation = {
    value: UserInputOptionType.NotEmpty,
    name: 'is not empty',
    requiresUserValue: false,
  };
  public readonly IS_SYSTEM_TYPE: FilterOperation = {
    value: UserInputOptionType.Equals,
    name: 'is system type',
    requiresUserValue: true,
  };
  public readonly IS_NOT_SYSTEM_TYPE: FilterOperation = {
    value: UserInputOptionType.DoesNotEqual,
    name: 'is not system type',
    requiresUserValue: true,
  };
  public readonly EMPTY_BOOLEAN: FilterOperation = {
    value: UserInputOptionType.Equals,
    name: 'is empty',
    requiresUserValue: false,
  };

  
 

  

  public getFilterOperations(
    filterDataType: FilterDataType,
    systemIdIncludeMultiType: boolean,
    booleanShowEmpty: boolean,
    referenceDataName?: string,
  ): Observable<FilterOperation[]> {
    switch (filterDataType) {
      case FilterDataType.Number:
      case FilterDataType.String:
        return of([
          this.CONTAINS,
          this.DOES_NOT_CONTAIN,
          this.EQUALS,
          this.DOES_NOT_EQUAL,
          this.EMPTY,
          this.NOT_EMPTY,
        ]);
      case FilterDataType.Boolean: {
        const ops = [this.EQUALS, this.DOES_NOT_EQUAL];
        if (booleanShowEmpty) {
          ops.push(this.EMPTY_BOOLEAN);
        }
        return of(ops);
      }
      case FilterDataType.Date:
        return of([
          this.EQUALS,
          this.LESS_THAN,
          this.LESS_THAN_OR_EQUAL,
          this.GREATER_THAN,
          this.GREATER_THAN_OR_EQUAL,
          this.RANGE,
          this.EMPTY,
          this.NOT_EMPTY,
        ]);
      case FilterDataType.DateTime:
        return of([
          this.EQUALS,
          this.NOTINRANGE,
          this.LESS_THAN,
          this.LESS_THAN_OR_EQUAL,
          this.GREATER_THAN,
          this.GREATER_THAN_OR_EQUAL,
          this.RANGE,
        ]);
      case FilterDataType.Reference: {
        // TODO load options from referenceService and convert to FilterOptionation[]
        if (referenceDataName?.length) {
          //   return this.selectListService.getSelectList(referenceDataName).pipe(
          //     map(refData =>
          //       refData[0].options.map(
          //         (o): FilterOperation => ({
          //           value: o.value,
          //           name: o.name,
          //         }),
          //       ),
          //     ),
          //   );
          return of([
            this.EQUALS,
            this.DOES_NOT_EQUAL,
            this.CONTAINS,
            this.DOES_NOT_CONTAIN,
            this.EMPTY,
            this.NOT_EMPTY,
          ]);
        } else {
          // TODO remove after implementing referenceDataName
          return of([
            this.EQUALS,
            this.DOES_NOT_EQUAL,
            this.CONTAINS,
            this.DOES_NOT_CONTAIN,
            this.EMPTY,
            this.NOT_EMPTY,
          ]);
          // throw new Error(
          //   'A datatable with filtering enabled must include a filterDataType for every column definition.',
          // );
        }
      }
      case FilterDataType.SystemId: {
        const ops = [this.EQUALS, this.DOES_NOT_EQUAL, this.EMPTY, this.NOT_EMPTY];
        if (systemIdIncludeMultiType) {
          ops.splice(2, 0, this.IS_SYSTEM_TYPE, this.IS_NOT_SYSTEM_TYPE);
        }
        return of(ops);
      }
      case FilterDataType.None:
        console.warn(
          "A column with filtering set to 'None' was not correctly disabled. The user will not be able to select a Filter Operator",
        );
        return of([]);
      default:
        throw new Error(
          'A datatable with filtering enabled must include a filterDataType for every column definition.',
        );
    }
  }

  public getFilterOperationDisplay = (id: UserInputOptionType): string =>
    [
      this.CONTAINS,
      this.DOES_NOT_CONTAIN,
      this.EQUALS,
      this.DOES_NOT_EQUAL,
      this.LESS_THAN,
      this.LESS_THAN_OR_EQUAL,
      this.GREATER_THAN,
      this.GREATER_THAN_OR_EQUAL,
      this.RANGE,
      this.NOTINRANGE,
      this.EMPTY,
      this.NOT_EMPTY,
      this.IS_SYSTEM_TYPE,
      this.IS_NOT_SYSTEM_TYPE,
      this.EMPTY_BOOLEAN,
    ].find(op => op.value === id)?.name || '';

  public getFilterOperationHint = (
    filterDataType: FilterDataType,
    filterOperation: FilterOperation,
  ): string => {
    if (filterOperation && filterDataType) {
      if (filterDataType === FilterDataType.Date) {
        return filterOperation === this.RANGE ? 'MM/DD/YYYY - MM/DD/YYYY' : 'MM/DD/YYYY';
      } else if (filterDataType === FilterDataType.DateTime) {
        return filterOperation === this.RANGE ? 'MM/DD/YYYY - MM/DD/YYYY' : 'MM/DD/YYYY';
      } else if (
        filterDataType === FilterDataType.SystemId &&
        [this.IS_SYSTEM_TYPE, this.IS_NOT_SYSTEM_TYPE].includes(filterOperation)
      ) {
        return 'Enter a two letter type code';
      } else if (filterOperation === this.CONTAINS || filterOperation === this.DOES_NOT_CONTAIN) {
        return 'A minimum of two characters is required to filter';
      }
    }
  };
}
